import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import classNames from "classnames";

import utils from "@utils";
import { V3_INVOICE_TYPE_ID } from "@constants";
import { PaymentService } from "@services";

import "./AddToInvoiceModal.scss";

const AddToInvoiceModal = ({ show, onHide, router, jobs, user, onCreateNewInvoice,
}) => {
	const { lang } = global;
	const [receipts, setReceipts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedInvoice, setSelectedInvoice] = useState("");
	const [job] = Object.values(jobs);
	const { ClientId, ClientName } = job || {};

	const handleOnSave = async () => {
		try {
			setLoading(true);

			const jobIds = Object.values(jobs)
				.map(({ Id }) => Id)
				.join(",");
			const queryParams = {
				clientId: ClientId,
				estimate: false,
				id: selectedInvoice,
				jobs: jobIds,
			};
			router({ pathname: "/payments/manualinvoice", query: queryParams });
		} catch {
			utils.notify({
				type: "error",
				message: lang.t("Error adding to invoice"),
			});
		} finally {
			setLoading(false);
		}
	};

	const loadReceipts = async () => {
		try {
			setLoading(true);

			// Officer invoices should be created by the officer and nevver added by an agency.
			const invoiceTypeId = V3_INVOICE_TYPE_ID.AGENCY;
			const queryParams = {
				displayCount: 100,
				pageNumber: 1,
				archived: false,
				paid: false,
				clientId: ClientId,
				sortBy: "InvoiceNumber",
				asc: true,
				finalized: false,
				pending: true,
				invoiceType: invoiceTypeId,
			};
			const {
				data: { ItemList },
			} = await PaymentService.getNonRKPayInvoicesV4(() => { }, queryParams)
			setReceipts(ItemList);
		} catch {
			utils.notify({
				type: "error",
				message: lang.t("Error loading receipts"),
			});
		} finally {
			setLoading(false);
		}
	};

	const onCancel = () => {
		setLoading(false);
		setSelectedInvoice("");
		onHide();
	};

	useEffect(() => {
		if (show && ClientId) {
			loadReceipts();
		}
	}, [show, jobs]);

	return (
		<Modal
			id="add-jobs-to-invoice"
			show={show}
			onHide={onCancel}
		>
			<Modal.Header closeButton>
				<Modal.Title className="text-center">{lang.t("Add Job to invoice")}</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<div className="margin-bottom-lg">{ClientName}</div>

				<div className="flex flex-justify-content-end">
					<button
						type="button"
						className="btn btn-link"
						onClick={onCreateNewInvoice}
					>
						+ {lang.t("Create New Invoice")}
					</button>
				</div>

				<div
					className="row hidden-xs hidden-sm margin-top-15"
					id="add-jobs-to-invoice-header"
				>
					<div className="col-md-3">
						<strong>{lang.t("Invoice")} #</strong>
					</div>
					<div className="col-md-3">
						<strong>{lang.t("Created Date")}</strong>
					</div>
					<div className="col-md-7" />
				</div>

				<div className="row">
					<div id="add-jobs-to-invoice-body">
						<For
							each="item"
							of={receipts}
						>
							<div
								key={item.Id}
								className={classNames("v-align-sm", {
									selected: item.Id === selectedInvoice,
								})}
								onClick={() => setSelectedInvoice(item.Id)}
								role="button"
								tabIndex="0"
								onKeyPress={null}
							>
								<div
									className="col-sm-12 col-md-3 truncate"
									title={item.InvoiceNumber}
								>
									{item.InvoiceNumber}
								</div>
								<div
									className="col-sm-12 col-md-3 truncate"
									title={item.StartDateTime}
								>
									{utils.formatDate(
										item.IssueDate,
										"en-US",
										user.TimeZoneId,
										"numeric",
									)}
								</div>
							</div>
						</For>
						<If condition={loading}>
							<div className="text-center">
								<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
							</div>
						</If>
					</div>
				</div>
			</Modal.Body>

			<Modal.Footer className="text-center">
				<button
					type="button"
					className="btn btn-primary"
					onClick={handleOnSave}
					disabled={!selectedInvoice}
				>
					{lang.t("Add to Invoice")}
				</button>
			</Modal.Footer>
		</Modal>
	);
};

AddToInvoiceModal.propTypes = {
	show: PropTypes.bool.isRequired,
	onHide: PropTypes.func.isRequired,
	router: PropTypes.object.isRequired,
	jobs: PropTypes.array.isRequired,
	onCreateNewInvoice: PropTypes.func.isRequired,
	user: PropTypes.object,
};

AddToInvoiceModal.defaultProps = {
	user: {},
};

export default AddToInvoiceModal;
